import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Calendar, Lock, Unlock, AlertTriangle, Check,
  ChevronLeft, ChevronRight, Edit3, FileText,
  Save, X, Plus, Trash2
} from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import { getWeekStatus, initializeWeek, lockWeek, unlockWeek,
  getWeekEntries, createHerdEntry, updateHerdEntry, deleteHerdEntry,
  getWeekNotes, saveDailyNote,
  getActiveHerds, getPaddocks, closeOpenEvent, reopenEvent
} from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

// Helper functions
function getMonday(date) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

function formatDateForAPI(date) {
  // Use local time, not UTC, to avoid timezone shifting dates
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getWeekDates(weekStart) {
  const dates = []
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart)
    date.setDate(date.getDate() + i)
    dates.push(date)
  }
  return dates
}

function isToday(date) {
  const today = new Date()
  return date.toDateString() === today.toDateString()
}

function isPastWeek(weekStart) {
  const today = new Date()
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)
  return weekEnd < today
}

const herdColors = {
  'Main': 'bg-blue-500',
  'A': 'bg-green-500',
  'B': 'bg-purple-500',
  'C': 'bg-orange-500',
}

const statusColors = {
  'pending': 'bg-gray-100 text-gray-600',
  'draft': 'bg-yellow-100 text-yellow-700',
  'locked': 'bg-green-100 text-green-700',
  'owner_unlocked': 'bg-orange-100 text-orange-700',
}

const statusLabels = {
  'pending': 'Not Started',
  'draft': 'Draft',
  'locked': 'Locked',
  'owner_unlocked': 'Unlocked by Owner',
}

export default function GrazingCalendar({ ranchId }) {
  const { canWrite } = useAuth()
  const [selectedWeekStart, setSelectedWeekStart] = useState(getMonday(new Date()))
  const [activeTab, setActiveTab] = useState('calendar') // 'calendar', 'entries', 'notes'
  const [showLockModal, setShowLockModal] = useState(false)
  const [showUnlockModal, setShowUnlockModal] = useState(false)
  const queryClient = useQueryClient()

  const weekStartStr = formatDateForAPI(selectedWeekStart)
  const weekDates = useMemo(() => getWeekDates(selectedWeekStart), [selectedWeekStart])

  // Fetch week status
  const { data: weekStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['weekStatus', ranchId, weekStartStr],
    queryFn: () => getWeekStatus(ranchId, weekStartStr),
  })

  // Fetch active herds
  const { data: herds, isLoading: herdsLoading } = useQuery({
    queryKey: ['activeHerds', ranchId],
    queryFn: () => getActiveHerds(ranchId),
  })

  // Fetch week entries - always fetch to get open events from previous weeks
  // even if current week is 'pending' (open events should still display)
  const { data: entries } = useQuery({
    queryKey: ['weekEntries', ranchId, weekStartStr],
    queryFn: () => getWeekEntries(ranchId, weekStartStr),
    enabled: !!weekStatus, // Fetch for all weeks, not just initialized ones
  })

  // Fetch week notes
  const { data: notes } = useQuery({
    queryKey: ['weekNotes', ranchId, weekStartStr],
    queryFn: () => getWeekNotes(ranchId, weekStartStr),
    enabled: !!weekStatus && weekStatus.status !== 'pending',
  })

  // Fetch paddocks
  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  // Initialize week mutation
  const initMutation = useMutation({
    mutationFn: () => initializeWeek(ranchId, weekStartStr),
    onSuccess: () => {
      queryClient.invalidateQueries(['weekStatus', ranchId, weekStartStr])
      queryClient.invalidateQueries(['weekEntries', ranchId, weekStartStr])
    },
  })

  // Navigation
  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeekStart)
    newDate.setDate(newDate.getDate() + (direction * 7))
    setSelectedWeekStart(newDate)
  }

  const canEdit = canWrite && (weekStatus?.can_edit ?? true)
  const isLocked = weekStatus?.status === 'locked'
  const isPast = isPastWeek(selectedWeekStart)

  // Get entry for a specific herd
  const getEntryForHerd = (herd) => {
    return entries?.find(e => e.herd === herd)
  }

  // Get all paddocks for herd on specific date (may be multiple if herd moved that day)
  // Accepts either a Date object or a date string (YYYY-MM-DD)
  const getPaddocksForHerdOnDate = (herd, dateOrString) => {
    const herdEntries = entries?.filter(e => e.herd === herd) || []
    const dateStr = typeof dateOrString === 'string' ? dateOrString : formatDateForAPI(dateOrString)
    const weekStartStr = formatDateForAPI(weekDates[0])
    const weekEndStr = formatDateForAPI(weekDates[6])
    const paddocks = []

    for (const entry of herdEntries) {
      const isOpenEvent = entry.is_open_event || false

      // For open events, use open_event_entry_date as the start
      // For regular entries, use entry_date
      // Ensure we have a string for comparison
      let entryStart = isOpenEvent
        ? (entry.open_event_entry_date || entry.entry_date)
        : entry.entry_date

      // Handle case where date might be a Date object or null
      if (!entryStart) continue
      if (typeof entryStart !== 'string') {
        entryStart = String(entryStart).split('T')[0] // Handle ISO datetime strings
      }

      // For entries without exit, the herd is still in the paddock through end of week
      let entryEnd = entry.exit_date || weekEndStr
      if (typeof entryEnd !== 'string') {
        entryEnd = String(entryEnd).split('T')[0]
      }

      // Check if this date falls within this entry's range
      // For open events from previous weeks, entryStart may be before weekStart
      // but we still show them for the current week
      const dateInRange = dateStr >= entryStart && dateStr <= entryEnd

      // For open events, also check if the current date is >= week start
      // (the event started before this week and continues into this week)
      const openEventInWeek = isOpenEvent && dateStr >= weekStartStr && dateStr <= entryEnd

      if (dateInRange || openEventInWeek) {
        // Determine if this is an entry day (only for entries that started THIS week)
        const entryThisWeek = !isOpenEvent && dateStr === entry.entry_date && entry.entry_date >= weekStartStr

        paddocks.push({
          id: entry.paddock_id,
          code: entry.paddock_code || '??',  // Fallback if code is missing
          name: entry.paddock_name || 'Unknown',
          isEntry: entryThisWeek,
          isExit: dateStr === entry.exit_date,
          isOpenEvent: isOpenEvent,
          // Show "continuing" indicator on first day of week for open events
          isContinuing: isOpenEvent && dateStr === weekStartStr
        })
      }
    }

    return paddocks
  }

  // Legacy function for backwards compatibility with NotesView
  const getPaddockForHerdOnDate = (herd, date) => {
    const paddocks = getPaddocksForHerdOnDate(herd, date)
    return paddocks.length > 0 ? paddocks[paddocks.length - 1] : null
  }

  // Check if date is a movement day (entry or exit)
  const isMovementDay = (herd, date) => {
    const herdEntries = entries?.filter(e => e.herd === herd) || []
    const dateStr = formatDateForAPI(date)
    return herdEntries.some(e => e.entry_date === dateStr || e.exit_date === dateStr)
  }

  // Get note for herd on date
  const getNoteForHerdDate = (herd, date) => {
    const dateStr = formatDateForAPI(date)
    return notes?.find(n => n.herd === herd && n.note_date === dateStr)
  }

  if (statusLoading || herdsLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Weekly Grazing Calendar"
        subtitle="Record and lock-in weekly grazing locations"
        icon={Calendar}
      />

      {/* Week Navigation & Status */}
      <div className="section-card p-4 mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateWeek(-1)}
            className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="h-5 w-5" />
            Previous
          </button>

          <div className="text-center">
            <h2 className="text-lg font-semibold">
              {selectedWeekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {' '}
              {weekDates[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[weekStatus?.status || 'pending']}`}>
                {statusLabels[weekStatus?.status || 'pending']}
              </span>
              {weekStatus?.status === 'pending' && canWrite && (
                <button
                  onClick={() => initMutation.mutate()}
                  disabled={initMutation.isPending}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {initMutation.isPending ? 'Starting...' : 'Start Week'}
                </button>
              )}
              {weekStatus?.status !== 'pending' && canEdit && isPast && (
                <button
                  onClick={() => setShowLockModal(true)}
                  className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                >
                  <Lock className="h-4 w-4" />
                  Lock Week
                </button>
              )}
              {isLocked && (
                <button
                  onClick={() => setShowUnlockModal(true)}
                  className="flex items-center gap-1 px-3 py-1 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700"
                >
                  <Unlock className="h-4 w-4" />
                  Request Unlock
                </button>
              )}
            </div>
          </div>

          <button
            onClick={() => navigateWeek(1)}
            className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Next
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'calendar'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Calendar className="h-4 w-4 inline mr-2" />
            Calendar View
          </button>
          <button
            onClick={() => setActiveTab('entries')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'entries'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Edit3 className="h-4 w-4 inline mr-2" />
            Entry/Exit
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === 'notes'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Daily Notes
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {weekStatus?.status === 'pending' ? (
        <>
          {/* Check if there are open events to display even for pending weeks */}
          {entries?.some(e => e.is_open_event) ? (
            <>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-orange-700">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-medium">Week not started</span>
                </div>
                <p className="text-sm text-orange-600 mt-1">
                  Showing continuing grazing from previous weeks. Click "Start Week" to add new entries.
                </p>
              </div>
              {activeTab === 'calendar' && (
                <CalendarView
                  herds={herds}
                  weekDates={weekDates}
                  entries={entries}
                  notes={notes}
                  getPaddocksForHerdOnDate={getPaddocksForHerdOnDate}
                  isMovementDay={isMovementDay}
                  getNoteForHerdDate={getNoteForHerdDate}
                  isLocked={false}
                />
              )}
              {activeTab !== 'calendar' && (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <p className="text-gray-500">Start the week to access Entry/Exit and Notes tabs.</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">Week Not Started</h3>
              <p className="text-gray-500 mb-4">Click "Start Week" to begin entering grazing data for this week.</p>
            </div>
          )}
        </>
      ) : (
        <>
          {activeTab === 'calendar' && (
            <CalendarView
              herds={herds}
              weekDates={weekDates}
              entries={entries}
              notes={notes}
              getPaddocksForHerdOnDate={getPaddocksForHerdOnDate}
              isMovementDay={isMovementDay}
              getNoteForHerdDate={getNoteForHerdDate}
              isLocked={isLocked}
            />
          )}
          {activeTab === 'entries' && (
            <EntriesView
              ranchId={ranchId}
              weekStartStr={weekStartStr}
              weekDates={weekDates}
              herds={herds}
              entries={entries}
              paddocks={paddocks}
              canEdit={canEdit}
            />
          )}
          {activeTab === 'notes' && (
            <NotesView
              ranchId={ranchId}
              weekStartStr={weekStartStr}
              weekDates={weekDates}
              herds={herds}
              entries={entries}
              notes={notes}
              getPaddockForHerdOnDate={getPaddockForHerdOnDate}
              canEdit={canEdit}
            />
          )}
        </>
      )}

      {/* Lock Modal */}
      {showLockModal && (
        <LockWeekModal
          ranchId={ranchId}
          weekStartStr={weekStartStr}
          weekStatus={weekStatus}
          herds={herds}
          entries={entries}
          onClose={() => setShowLockModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['weekStatus'])
            queryClient.invalidateQueries(['weekEntries'])
            setShowLockModal(false)
          }}
        />
      )}

      {/* Unlock Modal */}
      {showUnlockModal && (
        <UnlockWeekModal
          ranchId={ranchId}
          weekStartStr={weekStartStr}
          onClose={() => setShowUnlockModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['weekStatus'])
            setShowUnlockModal(false)
          }}
        />
      )}
    </div>
  )
}

// Calendar View Component - Shows paddock stays per day
function CalendarView({ herds, weekDates, entries, notes, getPaddocksForHerdOnDate, isMovementDay, getNoteForHerdDate, isLocked }) {
  return (
    <div className="section-card">
      {/* Header Row - Dates */}
      <div className="grid grid-cols-8 border-b">
        <div className="p-3 bg-earth-50 font-medium text-earth-700 border-r">Herd</div>
        {weekDates.map((date, idx) => (
          <div
            key={idx}
            className={`p-2 text-center text-sm border-r ${
              isToday(date) ? 'bg-green-50' : 'bg-earth-50'
            }`}
          >
            <div className="font-medium text-earth-700">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
            <div className={`text-xs ${isToday(date) ? 'text-green-600 font-bold' : 'text-earth-500'}`}>
              {date.getDate()}
            </div>
          </div>
        ))}
      </div>

      {/* Herd Rows */}
      {herds?.map(herd => (
        <div key={herd.herd} className="grid grid-cols-8 border-b last:border-b-0">
          <div className="p-3 bg-earth-50 font-medium text-earth-700 border-r">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${herdColors[herd.herd] || 'bg-gray-500'}`} />
              <span>{herd.herd === 'Main' ? 'Main' : `Herd ${herd.herd}`}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">{herd.cattle_count} head</div>
          </div>
          {weekDates.map((date, idx) => {
            const paddocks = getPaddocksForHerdOnDate(herd.herd, date)
            const note = getNoteForHerdDate(herd.herd, date)

            return (
              <div
                key={idx}
                className={`p-1 border-r min-h-20 ${isToday(date) ? 'bg-green-50' : ''}`}
              >
                {paddocks.map((paddock, pIdx) => (
                  <div
                    key={pIdx}
                    className={`p-1 rounded text-xs mb-1 ${
                      paddock.isOpenEvent
                        ? 'bg-orange-400 text-white border border-orange-600'
                        : `${herdColors[herd.herd] || 'bg-gray-500'} text-white`
                    }`}
                  >
                    <div className="font-medium flex items-center justify-between">
                      <span>{paddock.code}</span>
                      <span className="text-yellow-200 text-xs">
                        {paddock.isContinuing && '...'}
                        {paddock.isEntry && '→'}
                        {paddock.isExit && '←'}
                      </span>
                    </div>
                  </div>
                ))}
                {note?.notes && (
                  <div className="text-xs text-gray-500 truncate" title={note.notes}>
                    <FileText className="h-3 w-3 inline mr-1" />
                    Note
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ))}

      {/* Legend */}
      <div className="p-4 bg-gray-50 border-t">
        <div className="flex flex-wrap gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span>This Week</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-400 border border-orange-600 rounded"></div>
            <span>Continued Stay</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-blue-500 text-yellow-200 px-1 rounded text-xs">...</span>
            <span>Continuing</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-blue-500 text-yellow-200 px-1 rounded text-xs">→</span>
            <span>Entry</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-blue-500 text-yellow-200 px-1 rounded text-xs">←</span>
            <span>Exit</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span>Has Note</span>
          </div>
          {isLocked && (
            <div className="flex items-center gap-2 text-green-600">
              <Lock className="h-4 w-4" />
              <span>Week Locked</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Entries View Component - Shows multiple paddock stays per herd
function EntriesView({ ranchId, weekStartStr, weekDates, herds, entries, paddocks, canEdit }) {
  const queryClient = useQueryClient()
  const { userId } = useAuth()

  const createMutation = useMutation({
    mutationFn: (data) => createHerdEntry(ranchId, weekStartStr, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['weekEntries', ranchId, weekStartStr])
      queryClient.invalidateQueries(['weekStatus', ranchId, weekStartStr])
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ entryId, data }) => updateHerdEntry(ranchId, weekStartStr, entryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['weekEntries', ranchId, weekStartStr])
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (entryId) => deleteHerdEntry(ranchId, weekStartStr, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries(['weekEntries', ranchId, weekStartStr])
      queryClient.invalidateQueries(['weekStatus', ranchId, weekStartStr])
    },
  })

  // Mutations for open events (from previous weeks)
  const closeEventMutation = useMutation({
    mutationFn: ({ eventId, exitDate }) => closeOpenEvent(ranchId, eventId, exitDate),
    onSuccess: () => {
      queryClient.invalidateQueries(['weekEntries', ranchId, weekStartStr])
      queryClient.invalidateQueries(['activeHerds', ranchId])
    },
  })

  const reopenEventMutation = useMutation({
    mutationFn: (eventId) => reopenEvent(ranchId, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries(['weekEntries', ranchId, weekStartStr])
      queryClient.invalidateQueries(['activeHerds', ranchId])
    },
  })

  // Group entries by herd
  const entriesByHerd = useMemo(() => {
    const grouped = {}
    herds?.forEach(h => { grouped[h.herd] = [] })
    entries?.forEach(e => {
      if (grouped[e.herd]) grouped[e.herd].push(e)
    })
    return grouped
  }, [herds, entries])

  return (
    <div className="space-y-6">
      {herds?.map(herd => (
        <HerdMovementsCard
          key={herd.herd}
          herd={herd}
          entries={entriesByHerd[herd.herd] || []}
          paddocks={paddocks}
          weekDates={weekDates}
          canEdit={canEdit}
          onCreate={(data) => createMutation.mutateAsync({ ...data, herd: herd.herd, created_by: userId })}
          onUpdate={(entryId, data) => updateMutation.mutateAsync({ entryId, data: { ...data, updated_by: userId } })}
          onDelete={(entryId) => deleteMutation.mutateAsync(entryId)}
          onCloseOpenEvent={(eventId, exitDate) => closeEventMutation.mutateAsync({ eventId, exitDate })}
          onReopenEvent={(eventId) => reopenEventMutation.mutateAsync(eventId)}
          isLoading={createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || closeEventMutation.isPending || reopenEventMutation.isPending}
        />
      ))}
    </div>
  )
}

// Herd Movements Card - Shows all paddock stays for a herd
function HerdMovementsCard({ herd, entries, paddocks, weekDates, canEdit, onCreate, onUpdate, onDelete, onCloseOpenEvent, onReopenEvent, isLoading }) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [closingEventId, setClosingEventId] = useState(null)
  const [exitDateForClose, setExitDateForClose] = useState('')
  const [error, setError] = useState('')

  const weekStart = weekDates[0]
  const weekEnd = weekDates[6]

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  const formatDateLong = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Separate open events from regular entries
  const openEvents = entries.filter(e => e.is_open_event)
  const regularEntries = entries.filter(e => !e.is_open_event)

  return (
    <div className="bg-white rounded-lg shadow p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-4 h-4 rounded-full ${herdColors[herd.herd] || 'bg-gray-500'}`} />
          <div>
            <h3 className="font-semibold">{herd.herd === 'Main' ? 'Main Herd' : `Herd ${herd.herd}`}</h3>
            <p className="text-sm text-gray-500">{herd.cattle_count} head</p>
          </div>
        </div>
        {canEdit && !showAddForm && (
          <button
            onClick={() => { setShowAddForm(true); setError(''); }}
            className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
          >
            <Plus className="h-4 w-4" />
            Add Paddock Stay
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Open Events from Previous Weeks */}
      {openEvents.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-2">
            Continuing from Previous Week
          </div>
          <div className="space-y-3">
            {openEvents.map((event) => (
              <div key={event.id} className="border-2 border-orange-200 bg-orange-50 rounded-lg p-3">
                {closingEventId === event.id ? (
                  // Exit date form for closing the event
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${herdColors[herd.herd] || 'bg-gray-500'}`} />
                      <span className="font-medium">{event.paddock_code} - {event.paddock_name}</span>
                      <span className="text-sm text-gray-500">
                        (entered {formatDateLong(event.open_event_entry_date)})
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-600">Exit Date:</label>
                      <input
                        type="date"
                        value={exitDateForClose}
                        min={formatDateForAPI(weekStart)}
                        max={formatDateForAPI(weekEnd)}
                        onChange={(e) => setExitDateForClose(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                      <button
                        onClick={async () => {
                          if (!exitDateForClose) return
                          setError('')
                          try {
                            await onCloseOpenEvent(event.grazing_event_id, exitDateForClose)
                            setClosingEventId(null)
                            setExitDateForClose('')
                          } catch (err) {
                            setError(err.response?.data?.detail || err.message || 'Failed to close event')
                          }
                        }}
                        disabled={isLoading || !exitDateForClose}
                        className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
                      >
                        {isLoading ? 'Saving...' : 'Save Exit'}
                      </button>
                      <button
                        onClick={() => { setClosingEventId(null); setExitDateForClose(''); }}
                        className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display view for open event
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4 text-orange-500" />
                        <span className="text-orange-600 text-xs font-medium">OPEN</span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {event.paddock_code} - {event.paddock_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          Entered {formatDateLong(event.open_event_entry_date)} — still in paddock
                        </div>
                      </div>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => { setClosingEventId(event.id); setExitDateForClose(''); setError(''); }}
                        className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-lg hover:bg-orange-200"
                      >
                        Set Exit Date
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <PaddockStayForm
          paddocks={paddocks}
          weekDates={weekDates}
          existingEntries={regularEntries}
          openEvents={openEvents}
          onSave={async (data) => {
            setError('')
            try {
              await onCreate(data)
              setShowAddForm(false)
            } catch (err) {
              setError(err.response?.data?.detail || err.message || 'Failed to add entry')
            }
          }}
          onCancel={() => setShowAddForm(false)}
          isLoading={isLoading}
        />
      )}

      {/* Regular Entries List (this week) */}
      {regularEntries.length > 0 && (
        <div className="space-y-3">
          {openEvents.length > 0 && (
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              This Week's Entries
            </div>
          )}
          {regularEntries.map((entry, idx) => (
            <div key={entry.id} className="border rounded-lg p-3">
              {editingId === entry.id ? (
                <PaddockStayForm
                  entry={entry}
                  paddocks={paddocks}
                  weekDates={weekDates}
                  existingEntries={regularEntries.filter(e => e.id !== entry.id)}
                  openEvents={openEvents}
                  onSave={async (data) => {
                    setError('')
                    try {
                      await onUpdate(entry.id, data)
                      setEditingId(null)
                    } catch (err) {
                      setError(err.response?.data?.detail || err.message || 'Failed to update entry')
                    }
                  }}
                  onCancel={() => setEditingId(null)}
                  isLoading={isLoading}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-400 text-sm">#{idx + 1}</span>
                    <div>
                      <div className="font-medium text-gray-900">
                        {entry.paddock_code} - {entry.paddock_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(entry.entry_date)}
                        {entry.exit_date && ` → ${formatDate(entry.exit_date)}`}
                        {!entry.exit_date && ' (still there)'}
                      </div>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setEditingId(entry.id); setError(''); }}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm('Delete this paddock stay?')) {
                            setError('')
                            try {
                              await onDelete(entry.id)
                            } catch (err) {
                              setError(err.response?.data?.detail || err.message || 'Failed to delete')
                            }
                          }
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {regularEntries.length === 0 && openEvents.length === 0 && !showAddForm && (
        <p className="text-gray-400 italic text-sm">No paddock stays recorded for this week.</p>
      )}
    </div>
  )
}

// Paddock Stay Form (used for add/edit)
function PaddockStayForm({ entry, paddocks, weekDates, existingEntries = [], openEvents = [], onSave, onCancel, isLoading }) {
  const [formData, setFormData] = useState({
    paddock_id: entry?.paddock_id || '',
    entry_date: entry?.entry_date || '',
    exit_date: entry?.exit_date || '',
  })
  const [validationError, setValidationError] = useState('')

  const weekStart = weekDates[0]
  const weekEnd = weekDates[6]

  // Calculate the earliest allowed entry date based on existing entries and open events
  const getEarliestEntryDate = () => {
    let earliest = formatDateForAPI(weekStart)

    // If there's an open event, the first entry should be on or after week start
    // (the open event covers until exit is set)
    if (openEvents.length > 0) {
      // Open events are still active, so new entries should come after they exit
      // For now, we allow entry on any day (user will set exit on open event first)
    }

    // Check existing entries - new entry should be on or after last exit date
    const sortedEntries = [...existingEntries].sort((a, b) =>
      (a.entry_date || '').localeCompare(b.entry_date || '')
    )

    const lastEntry = sortedEntries[sortedEntries.length - 1]
    if (lastEntry?.exit_date) {
      // Entry can be same day as exit or day after
      earliest = lastEntry.exit_date
    } else if (lastEntry?.entry_date && !lastEntry?.exit_date) {
      // There's an entry with no exit - shouldn't add another until that one has exit
      // But we'll just warn rather than block
    }

    return earliest
  }

  // Validate that entry creates a coherent timeline
  const validateCoherence = () => {
    const entryDate = formData.entry_date
    const exitDate = formData.exit_date

    if (!entryDate) return 'Entry date is required'

    const newEntryDateObj = new Date(entryDate + 'T00:00:00')
    const newExitDateObj = exitDate ? new Date(exitDate + 'T00:00:00') : null
    const weekStartStr = formatDateForAPI(weekStart)

    // Check for overlap with open events from previous weeks
    // Open events are still active (no exit date set yet) - they continue until closed
    for (const openEvent of openEvents) {
      const openEventStart = openEvent.open_event_entry_date || openEvent.entry_date
      if (!openEventStart) continue

      // The open event is still active through this entire week (and beyond)
      // A new entry can only be added AFTER setting an exit date on the open event
      // The only exception is if the entry date is AFTER the exit date that will be set
      // But since open event has no exit date yet, any new entry would overlap
      return `There's an active grazing event in ${openEvent.paddock_code || 'unknown paddock'} (started ${openEventStart}) that hasn't been exited yet. Set the exit date for that paddock first, then add the new entry.`
    }

    // Check for overlap with existing entries this week
    for (const existing of existingEntries) {
      if (!existing.entry_date) continue

      const existingStart = new Date(existing.entry_date + 'T00:00:00')
      const existingEnd = existing.exit_date
        ? new Date(existing.exit_date + 'T00:00:00')
        : new Date(formatDateForAPI(weekEnd) + 'T00:00:00') // If no exit, assume through end of week

      // Check if new entry starts during existing period
      if (newEntryDateObj >= existingStart && newEntryDateObj <= existingEnd) {
        // Allow same-day handoff (entry on same day as exit)
        if (!(existing.exit_date && entryDate === existing.exit_date)) {
          return `New entry (${entryDate}) overlaps with existing entry in ${existing.paddock_code} (${existing.entry_date} - ${existing.exit_date || 'ongoing'}). Cattle can only be in one paddock at a time.`
        }
      }

      // Check if new entry period overlaps with existing
      if (newExitDateObj) {
        // Check if new period contains or overlaps existing period
        if (newEntryDateObj <= existingStart && newExitDateObj >= existingStart) {
          return `New entry period (${entryDate} - ${exitDate}) overlaps with existing entry in ${existing.paddock_code} starting ${existing.entry_date}.`
        }
      }
    }

    // Sort all entries by date and check for gaps
    const allEntries = [...existingEntries, { entry_date: entryDate, exit_date: exitDate }]
      .sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || ''))

    // Check for gaps - cattle must be somewhere every day
    for (let i = 0; i < allEntries.length - 1; i++) {
      const current = allEntries[i]
      const next = allEntries[i + 1]

      if (current.exit_date && next.entry_date) {
        const exitDay = new Date(current.exit_date + 'T00:00:00')
        const nextEntryDay = new Date(next.entry_date + 'T00:00:00')
        const diffDays = Math.floor((nextEntryDay - exitDay) / (1000 * 60 * 60 * 24))

        if (diffDays > 1) {
          return `Gap detected: ${diffDays - 1} day(s) between exit (${current.exit_date}) and next entry (${next.entry_date}). Cattle must be in a paddock every day.`
        }
      }
    }

    // Check if there's an open entry without exit that would overlap
    const openEntry = existingEntries.find(e => !e.exit_date && e.entry_date)
    if (openEntry && entryDate <= formatDateForAPI(weekEnd)) {
      const openEntryDate = new Date(openEntry.entry_date + 'T00:00:00')
      if (newEntryDateObj >= openEntryDate) {
        return `There's already an open entry (${openEntry.paddock_code}) starting ${openEntry.entry_date} with no exit. Set its exit date first.`
      }
    }

    return null
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.paddock_id || !formData.entry_date) {
      return
    }

    const error = validateCoherence()
    if (error) {
      setValidationError(error)
      return
    }

    setValidationError('')
    onSave({
      paddock_id: formData.paddock_id,
      entry_date: formData.entry_date,
      exit_date: formData.exit_date || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 bg-gray-50 p-3 rounded-lg">
      {validationError && (
        <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-xs">
          <AlertTriangle className="h-3 w-3 inline mr-1" />
          {validationError}
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Paddock *</label>
          <select
            value={formData.paddock_id}
            onChange={(e) => setFormData({ ...formData, paddock_id: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
            required
          >
            <option value="">Select...</option>
            {paddocks?.map(p => (
              <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Entry Date *</label>
          <input
            type="date"
            value={formData.entry_date}
            min={formatDateForAPI(weekStart)}
            max={formatDateForAPI(weekEnd)}
            onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Exit Date</label>
          <input
            type="date"
            value={formData.exit_date}
            min={formData.entry_date || formatDateForAPI(weekStart)}
            max={formatDateForAPI(weekEnd)}
            onChange={(e) => setFormData({ ...formData, exit_date: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || !formData.paddock_id || !formData.entry_date}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          <Save className="h-3 w-3" />
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// Notes View Component
function NotesView({ ranchId, weekStartStr, weekDates, herds, entries, notes, getPaddockForHerdOnDate, canEdit }) {
  const queryClient = useQueryClient()
  const { userId } = useAuth()
  const [editingNote, setEditingNote] = useState(null) // { herd, date }
  const [noteText, setNoteText] = useState('')
  const [weather, setWeather] = useState('')
  const [rainfall, setRainfall] = useState('')
  const [saveError, setSaveError] = useState('')

  const saveMutation = useMutation({
    mutationFn: (data) => saveDailyNote(ranchId, weekStartStr, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['weekNotes', ranchId, weekStartStr])
      queryClient.invalidateQueries(['weekStatus', ranchId, weekStartStr])
      setEditingNote(null)
      setNoteText('')
      setWeather('')
      setRainfall('')
      setSaveError('')
    },
    onError: (err) => {
      setSaveError(err.response?.data?.detail || err.message || 'Failed to save note')
    },
  })

  const openEditor = (herd, date) => {
    const dateStr = formatDateForAPI(date)
    const existingNote = notes?.find(n => n.herd === herd && n.note_date === dateStr)
    setEditingNote({ herd, date: dateStr })
    setNoteText(existingNote?.notes || '')
    setWeather(existingNote?.weather_conditions || '')
    setRainfall(existingNote?.rainfall_mm != null ? String(existingNote.rainfall_mm) : '')
    setSaveError('')
  }

  const saveNote = () => {
    if (!editingNote) return
    setSaveError('')
    // Pass the date string directly to avoid timezone conversion issues
    const paddock = getPaddockForHerdOnDate(editingNote.herd, editingNote.date)
    saveMutation.mutate({
      herd: editingNote.herd,
      note_date: editingNote.date,
      paddock_id: paddock?.id || null,
      notes: noteText,
      weather_conditions: weather || null,
      rainfall_mm: rainfall !== '' ? parseFloat(rainfall) : null,
      created_by: userId,
    })
  }

  return (
    <div className="section-card">
      {/* Header */}
      <div className="grid grid-cols-8 border-b">
        <div className="p-3 bg-earth-50 font-medium text-earth-700 border-r">Herd</div>
        {weekDates.map((date, idx) => (
          <div key={idx} className={`p-2 text-center text-sm border-r ${isToday(date) ? 'bg-green-50' : 'bg-earth-50'}`}>
            <div className="font-medium text-earth-700">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
            <div className={`text-xs ${isToday(date) ? 'text-green-600 font-bold' : 'text-earth-500'}`}>{date.getDate()}</div>
          </div>
        ))}
      </div>

      {/* Rows */}
      {herds?.map(herd => (
        <div key={herd.herd} className="grid grid-cols-8 border-b last:border-b-0">
          <div className="p-3 bg-earth-50 font-medium text-earth-700 border-r">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${herdColors[herd.herd] || 'bg-gray-500'}`} />
              <span className="text-sm">{herd.herd === 'Main' ? 'Main' : herd.herd}</span>
            </div>
          </div>
          {weekDates.map((date, idx) => {
            const dateStr = formatDateForAPI(date)
            const note = notes?.find(n => n.herd === herd.herd && n.note_date === dateStr)
            const paddock = getPaddockForHerdOnDate(herd.herd, date)
            const isEditingThis = editingNote?.herd === herd.herd && editingNote?.date === dateStr

            return (
              <div
                key={idx}
                className={`p-2 border-r min-h-24 ${isToday(date) ? 'bg-green-50' : ''} ${canEdit ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onClick={() => canEdit && !isEditingThis && openEditor(herd.herd, date)}
              >
                {paddock && (
                  <div className="text-xs text-gray-400 mb-1">{paddock.code}</div>
                )}
                {isEditingThis ? (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    {saveError && (
                      <div className="p-1 bg-red-50 border border-red-200 text-red-600 text-xs rounded">
                        {saveError}
                      </div>
                    )}
                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      className="w-full p-1 text-xs border rounded"
                      rows={2}
                      placeholder="Notes..."
                      autoFocus
                    />
                    <select
                      value={weather}
                      onChange={(e) => setWeather(e.target.value)}
                      className="w-full p-1 text-xs border rounded"
                    >
                      <option value="">Weather</option>
                      <option value="sunny">Sunny</option>
                      <option value="cloudy">Cloudy</option>
                      <option value="rainy">Rainy</option>
                      <option value="windy">Windy</option>
                    </select>
                    <input
                      type="number"
                      value={rainfall}
                      onChange={(e) => setRainfall(e.target.value)}
                      className="w-full p-1 text-xs border rounded"
                      step="0.1"
                      min="0"
                      placeholder="Rain gauge (mm)"
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={saveNote}
                        disabled={saveMutation.isPending}
                        className="flex-1 p-1 bg-green-600 text-white text-xs rounded disabled:opacity-50"
                      >
                        {saveMutation.isPending ? '...' : <Check className="h-3 w-3 mx-auto" />}
                      </button>
                      <button
                        onClick={() => { setEditingNote(null); setSaveError(''); }}
                        className="flex-1 p-1 bg-gray-200 text-gray-600 text-xs rounded"
                      >
                        <X className="h-3 w-3 mx-auto" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {note?.notes && (
                      <p className="text-xs text-gray-600 line-clamp-3">{note.notes}</p>
                    )}
                    {(note?.weather_conditions || note?.rainfall_mm != null) && (
                      <div className="flex items-center gap-1 flex-wrap">
                        {note?.weather_conditions && (
                          <span className="text-xs text-blue-500 capitalize">{note.weather_conditions}</span>
                        )}
                        {note?.rainfall_mm != null && (
                          <span className="text-xs text-blue-600 font-medium">{note.rainfall_mm}mm</span>
                        )}
                      </div>
                    )}
                    {!note?.notes && canEdit && (
                      <p className="text-xs text-gray-300 italic">Click to add</p>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

// Lock Week Modal
function LockWeekModal({ ranchId, weekStartStr, weekStatus, herds, entries, onClose, onSuccess }) {
  const { userId } = useAuth()
  const [confirmIncomplete, setConfirmIncomplete] = useState(false)
  const [error, setError] = useState('')

  const lockMutation = useMutation({
    mutationFn: () => lockWeek(ranchId, weekStartStr, {
      locked_by: userId,
      confirm_incomplete: confirmIncomplete,
    }),
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to lock week'),
  })

  // Count distinct herds that have entries (not total entries)
  const herdsWithEntries = new Set(entries?.map(e => e.herd) || []).size
  const herdsCount = herds?.length || 0
  const isComplete = herdsWithEntries >= herdsCount

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Lock Week
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="mb-4">
          <p className="text-gray-600 mb-2">
            You are about to lock the grazing calendar for this week. This will:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
            <li>Create grazing event records for all entries</li>
            <li>Prevent further edits to this week</li>
            <li>Aggregate daily notes into grazing events</li>
          </ul>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex justify-between text-sm">
            <span>Herds with entries:</span>
            <span className={isComplete ? 'text-green-600' : 'text-yellow-600'}>
              {herdsWithEntries} / {herdsCount}
            </span>
          </div>
          <div className="flex justify-between text-sm text-gray-500 mt-1">
            <span>Total paddock stays:</span>
            <span>{entries?.length || 0}</span>
          </div>
        </div>

        {!isComplete && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-700 font-medium">Incomplete Data</p>
                <p className="text-sm text-yellow-600">Not all herds have entries for this week.</p>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={confirmIncomplete}
                    onChange={(e) => setConfirmIncomplete(e.target.checked)}
                    className="h-4 w-4 text-yellow-600 rounded"
                  />
                  <span className="text-sm text-yellow-700">Lock anyway with incomplete data</span>
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => lockMutation.mutate()}
            disabled={lockMutation.isPending || (!isComplete && !confirmIncomplete)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Lock className="h-4 w-4" />
            {lockMutation.isPending ? 'Locking...' : 'Lock Week'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Unlock Week Modal
function UnlockWeekModal({ ranchId, weekStartStr, onClose, onSuccess }) {
  const { userId } = useAuth()
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')

  const unlockMutation = useMutation({
    mutationFn: () => unlockWeek(ranchId, weekStartStr, {
      override_by: userId,
      reason,
    }),
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to unlock week'),
  })

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Unlock className="h-5 w-5" />
          Request Unlock
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="mb-4">
          <p className="text-gray-600 mb-4">
            Only ranch owners can unlock a locked week. You must provide a reason for the unlock.
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason for unlock *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={3}
            placeholder="e.g., Incorrect paddock entry needs correction..."
            required
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => unlockMutation.mutate()}
            disabled={unlockMutation.isPending || !reason.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            <Unlock className="h-4 w-4" />
            {unlockMutation.isPending ? 'Unlocking...' : 'Unlock Week'}
          </button>
        </div>
      </div>
    </div>
  )
}
